import { FlightsClassTypes, fareClassToApiCabinMap } from 'constants/flight'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import Modal from 'LuxKit/Modal/Modal'
import React, { useCallback, useMemo, useState, useEffect } from 'react'
import { useIntl } from 'react-intl'
import FareClassCard from './FareClassCard'
import Caption from 'LuxKit/Typography/Caption'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import Breakpoint from 'components/Common/Breakpoint'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import LineSeatIcon from 'LuxKit/Icons/line/LineSeatIcon'
import LinePlayCircleIcon from 'LuxKit/Icons/line/LinePlayCircleIcon'
import LineRestaurantIcon from 'LuxKit/Icons/line/LineRestaurantIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import LineBagIcon from 'LuxKit/Icons/line/LineBagIcon'
import LineLuggageIcon from 'LuxKit/Icons/line/LineLuggageIcon'
import LineTicketIcon from 'LuxKit/Icons/line/LineTicketIcon'
import LineClochIcon from 'LuxKit/Icons/line/LineClochIcon'
import LineDoorIcon from 'LuxKit/Icons/line/LineDoorIcon'
import LineCarIcon from 'LuxKit/Icons/line/LineCarIcon'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Tooltip from 'LuxKit/Tooltip'
import Badge from 'LuxKit/Badge'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import { useFareClassPriceDifferencesPerAdult } from 'hooks/CharterFlights/useFareClassPriceDifferences'
import { EmptyArray } from 'lib/array/arrayUtils'
import useCharterFlightJourneysForHotelDates from 'hooks/CharterFlights/useCharterFlightJourneysForHotelDates'
import { MALDIVES_CHARTER_ROUTE } from 'api/lib/flightDealMap'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import { isSkyLoungeCompatible } from 'lib/flights/flightUtils'

const imageIds = {
  [FlightsClassTypes.ECONOMY]: 'maldives-economy',
  [FlightsClassTypes.PREMIUM]: 'maldives-economy-plus',
  [FlightsClassTypes.LUXROW]: 'maldives-lux-row',
  [FlightsClassTypes.BUSINESS]: 'maldives-business',
} as const

const baseFareClasses = [
  FlightsClassTypes.ECONOMY,
  FlightsClassTypes.PREMIUM,
  FlightsClassTypes.BUSINESS,
] as const

const fareClassesWithLuxRow = [
  FlightsClassTypes.ECONOMY,
  FlightsClassTypes.PREMIUM,
  FlightsClassTypes.LUXROW,
  FlightsClassTypes.BUSINESS,
] as const

type BaggageClass = FlightsClassTypes.ECONOMY | FlightsClassTypes.PREMIUM | FlightsClassTypes.LUXROW | FlightsClassTypes.BUSINESS

// Hardcoded baggage values for Maldives charter - do not use API values
const MALDIVES_CHARTER_BAGGAGE: Record<BaggageClass, { carryOn: string, checked: string }> = {
  [FlightsClassTypes.ECONOMY]: { carryOn: 'Carry-on 7kg', checked: 'Checked bag 23kg' },
  [FlightsClassTypes.PREMIUM]: { carryOn: 'Carry-on 7kg', checked: 'Checked bag 23kg' },
  [FlightsClassTypes.LUXROW]: { carryOn: 'Carry-on 7kg', checked: 'Checked bag 23kg' },
  [FlightsClassTypes.BUSINESS]: { carryOn: 'Carry-on 2 pieces up to 14kg, max 10kg per piece', checked: 'Checked bag 2 pieces, total 46kg, no more than 32kg per piece' },
}

interface Props {
  checkInDate?: string
  checkOutDate?: string
  occupancies?: Array<App.Occupants>
  selectedClassId: FlightsClassTypes
  allowedFareClasses?: Array<FlightsClassTypes>
}

/**
 * This is specific to the Maldives charter flights. It's not designed to be reusable for future charter flight offers.
 * Uses modal's resolve mechanism to return the selected fare class.
 */
function MaldivesCharterFlightFareClassModal({
  selectedClassId,
  checkInDate,
  checkOutDate,
  occupancies = EmptyArray,
  allowedFareClasses = EmptyArray,
}: Props) {
  const intl = useIntl()
  const modalElement = useModalElementContext<FlightsClassTypes>()

  const isLuxRowEnabled = !!useOptimizelyExperiment(OptimizelyFeatureFlags.luxRow)
  const isSkyLoungeCompatibleWithOccupancies = isSkyLoungeCompatible(occupancies)

  // Use local state to track selection within the modal
  const [localSelectedClass, setLocalSelectedClass] = useState<FlightsClassTypes>(selectedClassId)

  const onClassSelect = useCallback((fareClass: FlightsClassTypes) => {
    setLocalSelectedClass(fareClass)
  }, [])

  const closeModal = useCallback(() => {
    // Return the selected class via modal's resolve mechanism
    modalElement.resolve(localSelectedClass)
  }, [modalElement, localSelectedClass])

  const { fareFamilies } = useCharterFlightJourneysForHotelDates({
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    checkInDate,
    checkOutDate,
    occupancies,
    fareClass: selectedClassId,
  })

  const hasDates = !!(checkInDate && checkOutDate)

  const allFareClasses = useMemo(() => {
    const baseClasses = isLuxRowEnabled ? fareClassesWithLuxRow : baseFareClasses
    const allowedClasses: ReadonlyArray<FlightsClassTypes> = allowedFareClasses.length > 0 ? allowedFareClasses : baseClasses
    return baseClasses.filter(fareClass => allowedClasses.includes(fareClass))
  }, [isLuxRowEnabled, allowedFareClasses])

  const unavailableFareClasses = useMemo<Set<FlightsClassTypes>>(() => {
    if (!hasDates || !fareFamilies?.upsellFaresPerLeg) return new Set()
    const availableCabins = new Set(
      fareFamilies.upsellFaresPerLeg.flatMap(leg => leg.map(fare => fare.cabin)),
    )
    return new Set(
      allFareClasses.filter(fareClass => {
        if (fareClass === FlightsClassTypes.ECONOMY) return false
        return !availableCabins.has(fareClassToApiCabinMap[fareClass])
      }),
    )
  }, [hasDates, fareFamilies, allFareClasses])

  const firstAvailableClass = useMemo(() => {
    return allFareClasses.find(fareClass => !unavailableFareClasses.has(fareClass))
  }, [allFareClasses, unavailableFareClasses])

  useEffect(() => {
    if (unavailableFareClasses.has(localSelectedClass) && firstAvailableClass) {
      setLocalSelectedClass(firstAvailableClass)
    }
  }, [unavailableFareClasses, localSelectedClass, firstAvailableClass])

  // Calculate price differences relative to the currently selected class (before modal opened)
  const comparisonClasses = useMemo(
    () => allFareClasses.filter(c => c !== selectedClassId),
    [selectedClassId, allFareClasses],
  )

  const priceDifferences = useFareClassPriceDifferencesPerAdult({
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    baseClass: selectedClassId,
    comparisonClasses,
    checkInDate,
    checkOutDate,
    occupancies,
    selectedFareClass: selectedClassId,
  })

  const economyPriceDiff = priceDifferences[FlightsClassTypes.ECONOMY]
  const premiumPriceDiff = priceDifferences[FlightsClassTypes.PREMIUM]
  const businessPriceDiff = priceDifferences[FlightsClassTypes.BUSINESS]
  const luxRowPriceDiff = priceDifferences[FlightsClassTypes.LUXROW]

  const renderFareClassSubtitle = useCallback((fareClass: FlightsClassTypes, priceDiff: number | undefined) => {
    if (fareClass === FlightsClassTypes.ECONOMY && selectedClassId === FlightsClassTypes.ECONOMY) {
      return <Caption variant="large" weight="normal" colour="neutral-two">{intl.formatMessage({
        id: 'Jc/R3A4Vsm',
        defaultMessage: 'Included in bundle',
        description: 'Label for economy fare class when it is included in the charter flight bundle',
      })}
      </Caption>
    }
    if (selectedClassId === fareClass) {
      return undefined
    }
    if (priceDiff === undefined) {
      return undefined
    }
    return (
      <Caption variant="large" weight="normal" colour="neutral-two">
        <Group direction="horizontal" gap={4} verticalAlign="baseline">
          <PriceRowCaption>{intl.formatMessage({
            id: 'RUJ7Gofk5b',
            defaultMessage: 'From',
            description: 'Pricing label prefix before fare class price difference in charter flight modal',
          })}
          </PriceRowCaption>
          <PriceRowPrice price={priceDiff} saleUnit="person" size="S" signDisplay="always" />
        </Group>
      </Caption>
    )
  }, [selectedClassId, intl])

  // Use hardcoded baggage values - do not use API values
  const getBaggageCopy = useCallback((fareClass: BaggageClass) => MALDIVES_CHARTER_BAGGAGE[fareClass], [])

  const economyBaggageCopy = getBaggageCopy(FlightsClassTypes.ECONOMY)
  const premiumBaggageCopy = getBaggageCopy(FlightsClassTypes.PREMIUM)
  const luxRowBaggageCopy = getBaggageCopy(FlightsClassTypes.LUXROW)
  const businessBaggageCopy = getBaggageCopy(FlightsClassTypes.BUSINESS)

  const shouldShowEconomy = allFareClasses.includes(FlightsClassTypes.ECONOMY)
  const shouldShowPremium = allFareClasses.includes(FlightsClassTypes.PREMIUM)
  const shouldShowBusiness = allFareClasses.includes(FlightsClassTypes.BUSINESS)
  const shouldShowSkyLounge = allFareClasses.includes(FlightsClassTypes.LUXROW)

  // Shared localized strings for charter flight amenities
  const streamEntertainmentPersonalLabel = intl.formatMessage({
    id: 'jOMZWuWfYp',
    defaultMessage: 'Stream on-demand entertainment to your personal device',
    description: 'Charter flight amenity: in-flight entertainment streaming to personal device',
  })
  const complimentaryMealLabel = intl.formatMessage({
    id: 'BQRK/i0lCZ',
    defaultMessage: 'Complimentary meal and drinks service',
    description: 'Charter flight amenity: complimentary meal and drinks',
  })
  const economyPriorityBoardingLabel = intl.formatMessage({
    id: 'IJoHLf5zXk',
    defaultMessage: 'Priority Check In & Boarding',
    description: 'Charter flight amenity: priority boarding',
  })
  const skyloungeBusinessPriorityBoardingLabel = intl.formatMessage({
    id: 'QbpmHgAHGI',
    defaultMessage: 'Priority Check In, Security & Boarding',
    description: 'Charter flight amenity: priority check in, security & boarding',
  })
  const airportLoungeLabel = intl.formatMessage({
    id: 'EJFVD85t1B',
    defaultMessage: 'Relax with airport lounge before your flight',
    description: 'Charter flight amenity: airport lounge access',
  })
  const baggageLabel = intl.formatMessage({
    id: 'TtmcS1O5XR',
    defaultMessage: 'Baggage',
    description: 'Section heading for baggage allowance in charter flight fare class',
  })
  const cannotAccommodateLabel = intl.formatMessage({
    id: 'GgnmxKfmBm',
    defaultMessage: 'Cannot accommodate your number of passengers.',
    description: 'Error message when SkyLounge fare class cannot fit the number of passengers',
  })
  const unavailableForDatesLabel = intl.formatMessage({
    id: 'DjH0Ds8OG/',
    defaultMessage: 'Unavailable for selected dates.',
    description: 'Error message when a fare class is sold out or unavailable for the selected travel dates',
  })

  const skyLoungeDisabledMessage = !isSkyLoungeCompatibleWithOccupancies
    ? cannotAccommodateLabel
    : unavailableFareClasses.has(FlightsClassTypes.LUXROW)
      ? unavailableForDatesLabel
      : undefined

  const skyLoungeCard = isLuxRowEnabled && shouldShowSkyLounge ? <FareClassCard
    fareId={FlightsClassTypes.LUXROW}
    imageId={imageIds[FlightsClassTypes.LUXROW]}
    name="SkyLounge"
    subtitle={renderFareClassSubtitle(FlightsClassTypes.LUXROW, luxRowPriceDiff)}
    isSelected={localSelectedClass === FlightsClassTypes.LUXROW}
    onSelect={onClassSelect}
    disabled={!isSkyLoungeCompatibleWithOccupancies || unavailableFareClasses.has(FlightsClassTypes.LUXROW)}
    disabledMessage={skyLoungeDisabledMessage}
  >
    <VerticalSpacer gap={16}>
      <VerticalSpacer gap={8}>
        <CaptionTextBlock variant="medium" startIcon={<LineSeatIcon />}>{intl.formatMessage({
          id: 'IQUH3I0jSt',
          defaultMessage: 'Enjoy more space for two passengers to unwind with a row of four economy seats.',
          description: 'Charter flight SkyLounge amenity: extra space with four economy seats',
        })}
        </CaptionTextBlock>
        <CaptionTextBlock variant="medium" startIcon={<LinePlayCircleIcon />}>{streamEntertainmentPersonalLabel}</CaptionTextBlock>
        <CaptionTextBlock variant="medium" startIcon={<LineRestaurantIcon />}>{complimentaryMealLabel}</CaptionTextBlock>
        <CaptionTextBlock variant="medium" startIcon={<LineTicketIcon />}>{skyloungeBusinessPriorityBoardingLabel}</CaptionTextBlock>
        <CaptionTextBlock variant="medium" startIcon={<LineDoorIcon />}>{airportLoungeLabel}</CaptionTextBlock>
      </VerticalSpacer>
      <VerticalSpacer gap={8}>
        <BodyText variant="small" weight="bold">{baggageLabel}</BodyText>
        <CaptionTextBlock variant="medium" startIcon={<LineBagIcon />}>{luxRowBaggageCopy.carryOn}</CaptionTextBlock>
        <CaptionTextBlock variant="medium" startIcon={<LineLuggageIcon />}>{luxRowBaggageCopy.checked}</CaptionTextBlock>
      </VerticalSpacer>
    </VerticalSpacer>
  </FareClassCard> : null

  // SkyLounge appears before Business when available, after Business when disabled
  const cards = <>
    {shouldShowEconomy && (
      <FareClassCard
        fareId={FlightsClassTypes.ECONOMY}
        imageId={imageIds[FlightsClassTypes.ECONOMY]}
        name={intl.formatMessage({
          id: '9p+3l7DfrD',
          defaultMessage: 'Economy',
          description: 'Name for economy fare class in charter flight selection',
        })}
        subtitle={renderFareClassSubtitle(FlightsClassTypes.ECONOMY, economyPriceDiff)}
        isSelected={localSelectedClass === FlightsClassTypes.ECONOMY}
        onSelect={onClassSelect}
      >
        <VerticalSpacer gap={16}>
          <VerticalSpacer gap={8}>
            <CaptionTextBlock variant="medium" startIcon={<LineSeatIcon />}>{intl.formatMessage({
              id: 'F4yxnIGbGZ',
              defaultMessage: 'Main cabin standard seat',
              description: 'Charter flight economy amenity: standard seat description',
            })}
            </CaptionTextBlock>
            <CaptionTextBlock variant="medium" startIcon={<LinePlayCircleIcon />}>{streamEntertainmentPersonalLabel}</CaptionTextBlock>
            <CaptionTextBlock variant="medium" startIcon={<LineRestaurantIcon />}>{complimentaryMealLabel}</CaptionTextBlock>
          </VerticalSpacer>
          <VerticalSpacer gap={8}>
            <BodyText variant="small" weight="bold">{baggageLabel}</BodyText>
            <CaptionTextBlock variant="medium" startIcon={<LineBagIcon />}>{economyBaggageCopy.carryOn}</CaptionTextBlock>
            <CaptionTextBlock variant="medium" startIcon={<LineLuggageIcon />}>{economyBaggageCopy.checked}</CaptionTextBlock>
          </VerticalSpacer>
        </VerticalSpacer>
      </FareClassCard>
    )}

    {shouldShowPremium && (
      <FareClassCard
        fareId={FlightsClassTypes.PREMIUM}
        imageId={imageIds[FlightsClassTypes.PREMIUM]}
        name={intl.formatMessage({
          id: 't4dJKEAFC0',
          defaultMessage: 'Economy Plus',
          description: 'Name for economy plus fare class in charter flight selection',
        })}
        subtitle={renderFareClassSubtitle(FlightsClassTypes.PREMIUM, premiumPriceDiff)}
        isSelected={localSelectedClass === FlightsClassTypes.PREMIUM}
        onSelect={onClassSelect}
        disabled={unavailableFareClasses.has(FlightsClassTypes.PREMIUM)}
        disabledMessage={unavailableFareClasses.has(FlightsClassTypes.PREMIUM) ? unavailableForDatesLabel : undefined}
      >
        <VerticalSpacer gap={16}>
          <VerticalSpacer gap={8}>
            <CaptionTextBlock variant="medium" startIcon={<LineSeatIcon />}>{intl.formatMessage({
              id: 'bIUPAClOVQ',
              defaultMessage: 'Extra legroom and generous seat recline for enhanced comfort',
              description: 'Charter flight economy plus amenity: enhanced seat comfort description',
            })}
            </CaptionTextBlock>
            <CaptionTextBlock variant="medium" startIcon={<LinePlayCircleIcon />}>{streamEntertainmentPersonalLabel}</CaptionTextBlock>
            <CaptionTextBlock variant="medium" startIcon={<LineRestaurantIcon />}>{complimentaryMealLabel}</CaptionTextBlock>
            <CaptionTextBlock variant="medium" startIcon={<LineTicketIcon />}>{economyPriorityBoardingLabel}</CaptionTextBlock>
          </VerticalSpacer>
          <VerticalSpacer gap={8}>
            <BodyText variant="small" weight="bold">{baggageLabel}</BodyText>
            <CaptionTextBlock variant="medium" startIcon={<LineBagIcon />}>{premiumBaggageCopy.carryOn}</CaptionTextBlock>
            <CaptionTextBlock variant="medium" startIcon={<LineLuggageIcon />}>{premiumBaggageCopy.checked}</CaptionTextBlock>
          </VerticalSpacer>
        </VerticalSpacer>
      </FareClassCard>
    )}

    {isSkyLoungeCompatibleWithOccupancies && skyLoungeCard}

    {shouldShowBusiness && (
      <FareClassCard
        fareId={FlightsClassTypes.BUSINESS}
        imageId={imageIds[FlightsClassTypes.BUSINESS]}
        name={intl.formatMessage({
          id: 'AQXWJeJqqW',
          defaultMessage: 'Business',
          description: 'Name for business fare class in charter flight selection',
        })}
        subtitle={renderFareClassSubtitle(FlightsClassTypes.BUSINESS, businessPriceDiff)}
        isSelected={localSelectedClass === FlightsClassTypes.BUSINESS}
        onSelect={onClassSelect}
        disabled={unavailableFareClasses.has(FlightsClassTypes.BUSINESS)}
        disabledMessage={unavailableFareClasses.has(FlightsClassTypes.BUSINESS) ? unavailableForDatesLabel : undefined}
      >
        <VerticalSpacer gap={16}>
          <VerticalSpacer gap={8}>
            <CaptionTextBlock variant="medium" startIcon={<LineSeatIcon />}>{intl.formatMessage({
              id: 'c0TjZdvglW',
              defaultMessage: 'Luxury lie-flat seats with in-seat power for ultimate comfort and convenience',
              description: 'Charter flight business amenity: lie-flat seat description',
            })}
            </CaptionTextBlock>
            <CaptionTextBlock
              variant="medium"
              startIcon={<LineCarIcon />}
              endIcon={
                <Tooltip
                  description={intl.formatMessage({
                    id: '9YhUIqIqMu',
                    defaultMessage: 'SYD/BNE must book domestic flights with their charter package to be eligible',
                    description: 'Tooltip explaining eligibility for home to airport transfers in charter flights',
                  })}
                  placement="top"
                >
                  <LineInfoCircleIcon colour="neutral-two" size={14} />
                </Tooltip>
              }
            >
              <span style={{ display: 'inline-flex', verticalAlign: 'middle' }}><Badge mode="critical" kind="string">{intl.formatMessage({
                id: 'MGAfQMy5Jo',
                defaultMessage: 'New',
                description: 'Badge label for new charter flight business amenity',
              })}
              </Badge>
              </span>{' '}{intl.formatMessage({
                id: 'DrO4HKiA7k',
                defaultMessage: 'Home to airport transfers (max 40km) in MEL/SYD/BNE',
                description: 'Charter flight business amenity: home to airport transfer service',
              })}
            </CaptionTextBlock>
            <CaptionTextBlock variant="medium" startIcon={<LinePlayCircleIcon />}>{intl.formatMessage({
              id: 'MHWh67Ugh3',
              defaultMessage: 'Stream on-demand entertainment to provided handheld entertainment screens',
              description: 'Charter flight business amenity: entertainment on provided screens',
            })}
            </CaptionTextBlock>
            <CaptionTextBlock variant="medium" startIcon={<LineRestaurantIcon />}>{intl.formatMessage({
              id: 'ATmajwJ1Kk',
              defaultMessage: 'Gourmet dining options and drinks service',
              description: 'Charter flight business amenity: gourmet dining',
            })}
            </CaptionTextBlock>
            <CaptionTextBlock variant="medium" startIcon={<LineTicketIcon />}>{skyloungeBusinessPriorityBoardingLabel}</CaptionTextBlock>
            <CaptionTextBlock variant="medium" startIcon={<LineClochIcon />}>{intl.formatMessage({
              id: '9E/wC1ftCC',
              defaultMessage: 'Dedicated cabin crew for personalised service',
              description: 'Charter flight business amenity: dedicated cabin crew',
            })}
            </CaptionTextBlock>
            <CaptionTextBlock variant="medium" startIcon={<LineDoorIcon />}>{airportLoungeLabel}</CaptionTextBlock>
          </VerticalSpacer>
          <VerticalSpacer gap={8}>
            <BodyText variant="small" weight="bold">{baggageLabel}</BodyText>
            <CaptionTextBlock variant="medium" startIcon={<LineBagIcon />}>{businessBaggageCopy.carryOn}</CaptionTextBlock>
            <CaptionTextBlock variant="medium" startIcon={<LineLuggageIcon />}>{businessBaggageCopy.checked}</CaptionTextBlock>
          </VerticalSpacer>
        </VerticalSpacer>
      </FareClassCard>
    )}

    {!isSkyLoungeCompatibleWithOccupancies && skyLoungeCard}
  </>

  return <Modal
    title={intl.formatMessage({
      id: 'cQ1XgUVc5z',
      defaultMessage: 'Select a class',
      description: 'Title for charter flight fare class selection modal',
    })}
    subtitle="Maldivian · Airbus A330-200"
    primaryActionText={intl.formatMessage({
      id: '4tS92bLuwV',
      defaultMessage: 'Continue',
      description: 'Button text to confirm fare class selection in charter flight modal',
    })}
    onPrimaryActionClick={closeModal}
    onClose={closeModal}
    size="XL"
    height="full"
    tabletHeight="auto"
  >
    <Breakpoint max="tablet">
      <CardCarousel gap={12} snap="start" gutterSize={20}>
        {cards}
      </CardCarousel>
    </Breakpoint>

    <Breakpoint min="desktop">
      <Group direction="horizontal" gap={12} verticalAlign="stretch" horizontalAlign="stretch">
        {cards}
      </Group>
    </Breakpoint>
  </Modal>
}

export default MaldivesCharterFlightFareClassModal
